import React, { useState } from 'react';
import { Alert, Box, Button, Checkbox, FormControl, FormControlLabel, InputLabel, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import api from '../services/api';

const money = (amount) => new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(Number(amount || 0));
const id = (value) => String(value?._id || value || '');

export default function MatricContactOutcome({ application, groups = [], quote, quoteError, onUpdated }) {
  const [outcome, setOutcome] = useState(application.consultantRequest?.outcome || '');
  const [note, setNote] = useState(application.consultantRequest?.note || '');
  const [selection, setSelection] = useState(() => Object.fromEntries((application.selectedSubjects || []).map((subject) => [
    subject, id(groups.find((group) => group.subject === subject && (application.selectedGroupIds || []).some((value) => id(value) === id(group)))),
  ])));
  const [reviewed, setReviewed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [updatedQuote, setUpdatedQuote] = useState(null);
  const currentQuote = updatedQuote || quote;
  if (application.status !== 'consultant_requested') return application.consultantRequest?.outcome === 'confirmed'
    ? <Alert severity="success">Contacted and confirmed. The linked invoice is available for review and sending.</Alert> : null;

  const choices = (subject) => groups.filter((group) => group.active !== false && group.subject === subject
    && id(group.intakeId) === id(application.intakeId) && group.curriculum === application.examProfile?.curriculum
    && group.deliveryMode === application.deliveryMode && (group.deliveryMode !== 'in-person' || !application.venueId
      || (group.venueId || 'midrand') === application.venueId));
  const canSave = outcome && !busy && (outcome !== 'declined' || note.trim())
    && (outcome !== 'confirmed' || (currentQuote && reviewed && (application.selectedSubjects || []).length > 0
      && application.selectedSubjects.every((subject) => choices(subject).some((group) => id(group) === selection[subject]))));
  const save = async () => {
    if (!canSave) return;
    setBusy(true); setError(''); setSuccess('');
    try {
      const { data } = await api.patch(`/matric-rewrite/admin/applications/${application._id}/follow-up`, {
        outcome, note: note.trim(), ...(outcome === 'confirmed' ? {
          selectedGroupIds: application.selectedSubjects.map((subject) => selection[subject]),
          quoteConfirmation: { total: currentQuote.total, catalogueVersion: currentQuote.catalogueVersion },
        } : {}),
      });
      onUpdated(data.application);
      setSuccess(data.message);
    } catch (err) {
      setError(err.response?.data?.message || 'The contact outcome could not be saved.');
      if (err.response?.data?.quote) { setUpdatedQuote(err.response.data.quote); setReviewed(false); }
    } finally { setBusy(false); }
  };

  return <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
    <Stack spacing={2}>
      <Typography variant="h6">Record contact outcome</Typography>
      <FormControl fullWidth><InputLabel id="matric-contact-outcome-label">Contact outcome</InputLabel>
        <Select labelId="matric-contact-outcome-label" label="Contact outcome" value={outcome} disabled={busy}
          onChange={(event) => { setOutcome(event.target.value); setReviewed(false); setSuccess(''); }}>
          <MenuItem value="confirmed">Contacted and confirmed</MenuItem>
          <MenuItem value="declined">Contacted and declined</MenuItem>
          <MenuItem value="awaiting_decision">Contacted, awaiting a decision</MenuItem>
        </Select>
      </FormControl>
      {outcome === 'confirmed' && <>
        <Typography variant="body2">Confirm the timetable and programme price below. An invoice will be created for staff to review. No email is sent by this action.</Typography>
        {(application.selectedSubjects || []).map((subject, index) => <FormControl key={subject} fullWidth>
          <InputLabel id={`contact-group-${index}`}>{subject} class group</InputLabel>
          <Select labelId={`contact-group-${index}`} label={`${subject} class group`} value={selection[subject] || ''} disabled={busy}
            onChange={(event) => { setSelection((current) => ({ ...current, [subject]: event.target.value })); setReviewed(false); }}>
            {choices(subject).map((group) => <MenuItem key={id(group)} value={id(group)} disabled={group.reservedSeats >= group.capacity}>
              {group.name} - {(group.scheduleSlots?.length ? group.scheduleSlots : [group]).map((slot) => `${slot.dayOfWeek} ${slot.startTime}-${slot.endTime}`).join(', ')}
            </MenuItem>)}
          </Select>
          {!choices(subject).length && <Typography color="error" variant="caption">No compatible active class group. Configure one under Class groups before confirming.</Typography>}
        </FormControl>)}
        {currentQuote ? <Box><Typography>Registration fee: {money(currentQuote.registrationFee)}</Typography>
          <Typography>Tuition: {money(currentQuote.tuitionTotal)}</Typography><Typography fontWeight={700}>Invoice total: {money(currentQuote.total)}</Typography></Box>
          : <Alert severity="warning">{quoteError || 'The current programme price is loading or unavailable.'}</Alert>}
        <FormControlLabel control={<Checkbox checked={reviewed} disabled={busy} onChange={(event) => setReviewed(event.target.checked)} />}
          label="I confirm the subjects, timetable and invoice total." />
      </>}
      {outcome && outcome !== 'confirmed' && <Alert severity="info">Invoice creation and sending remain blocked for this outcome.</Alert>}
      <TextField label={outcome === 'declined' ? 'Decline reason' : 'Contact notes'} value={note} disabled={busy}
        onChange={(event) => setNote(event.target.value)} required={outcome === 'declined'} multiline minRows={2} inputProps={{ maxLength: 1000 }} />
      {error && <Alert severity="error">{error}</Alert>}{success && <Alert severity="success">{success}</Alert>}
      <Button variant="contained" disabled={!canSave} onClick={save}>{busy ? 'Saving...' : outcome === 'confirmed' ? 'Confirm & make invoice available' : 'Save contact outcome'}</Button>
    </Stack>
  </Box>;
}
